// src/components/UserInvitation.jsx
import React, { useState } from 'react';
import { inviteUser } from '../services/userManagementService';
import {
    USER_ROLES,
    PERMISSION_CATEGORIES,
    PERMISSION_LABELS,
    EDITOR_DEFAULT_PERMISSIONS,
    ADMIN_DEFAULT_PERMISSIONS
} from '../types/permissions';
import '../styles/components/UserInvitation.scss';

const UserInvitation = ({ accountId, currentUser, onUserInvited }) => {
    const [formData, setFormData] = useState({
        email: '',
        role: USER_ROLES.EDITOR
    });
    const [selectedPermissions, setSelectedPermissions] = useState(EDITOR_DEFAULT_PERMISSIONS);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleRoleChange = (role) => {
        setFormData({ ...formData, role });

        // ロールに応じてデフォルト権限を設定
        if (role === USER_ROLES.ADMIN) {
            setSelectedPermissions(ADMIN_DEFAULT_PERMISSIONS);
        } else {
            setSelectedPermissions(EDITOR_DEFAULT_PERMISSIONS);
        }
    };

    const handlePermissionChange = (permission, checked) => {
        if (checked) {
            setSelectedPermissions([...selectedPermissions, permission]);
        } else {
            setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.email) {
            setMessage('メールアドレスを入力してください');
            return;
        }

        try {
            setLoading(true);
            setMessage('');

            const invitationData = {
                email: formData.email,
                role: formData.role,
                permissions: selectedPermissions,
                invitedBy: currentUser.uid,
                inviterDisplayName: currentUser.displayName || currentUser.email,
                accountName: 'Suisui アカウント' // 後で実際のアカウント名に変更可能
            };

            await inviteUser(accountId, invitationData);

            setMessage('招待メールを送信しました');
            setFormData({ email: '', role: USER_ROLES.EDITOR });
            setSelectedPermissions(EDITOR_DEFAULT_PERMISSIONS);

            if (onUserInvited) {
                onUserInvited();
            }
        } catch (error) {
            setMessage('招待の送信に失敗しました: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-invitation">
            <div className="invitation-form">
                <h2>ユーザーを招待</h2>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">メールアドレス</label>
                        <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="example@example.com"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>ユーザーロール</label>
                        <div className="role-selection">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="role"
                                    value={USER_ROLES.ADMIN}
                                    checked={formData.role === USER_ROLES.ADMIN}
                                    onChange={(e) => handleRoleChange(e.target.value)}
                                />
                                <span>管理ユーザー（全権限）</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="role"
                                    value={USER_ROLES.EDITOR}
                                    checked={formData.role === USER_ROLES.EDITOR}
                                    onChange={(e) => handleRoleChange(e.target.value)}
                                />
                                <span>編集ユーザー（制限付き権限）</span>
                            </label>
                        </div>
                    </div>

                    {formData.role === USER_ROLES.EDITOR && (
                        <div className="form-group">
                            <label>権限設定</label>
                            <div className="permissions-grid">
                                {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => (
                                    <div key={categoryKey} className="permission-category">
                                        <h4>{category.name}</h4>
                                        {category.permissions.map(permission => (
                                            <label key={permission} className="permission-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPermissions.includes(permission)}
                                                    onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                                                />
                                                <span>{PERMISSION_LABELS[permission]}</span>
                                            </label>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="submit" disabled={loading} className="btn-primary">
                            {loading ? '送信中...' : '招待を送信'}
                        </button>
                    </div>
                </form>

                {message && (
                    <div className={`message ${message.includes('失敗') ? 'error' : 'success'}`}>
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserInvitation;
